import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  FiHome,
  FiUsers,
  FiBriefcase,
  FiHeart,
  FiAlertTriangle,
  FiSend,
  FiSettings,
  FiLogOut,
  FiChevronLeft,
  FiChevronRight,
  FiShoppingBag,
  FiFileText,
  FiTag,
  FiActivity,
  FiSearch,
  FiCalendar,
  FiCreditCard,
  FiDollarSign,
  FiSliders,
  FiGrid,
  FiX,
  FiShield,
  FiCpu,
  FiTrendingUp,
} from 'react-icons/fi';
import { GiCat } from 'react-icons/gi';
import { useAuth } from '../../context/AuthContext';
import { db, doc, onSnapshot, logoutAdmin } from '../../config/firebase';
import styles from './Sidebar.module.css';

const menuItems = [
  { path: '/', icon: FiHome, label: 'Dashboard' },
  { path: '/users', icon: FiUsers, label: 'Users' },
  { path: '/pets', icon: GiCat, label: 'Pets' },
  { path: '/businesses', icon: FiBriefcase, label: 'Businesses' },
  { path: '/vets', icon: FiHeart, label: 'Veterinarians' },
  { path: '/vet-bookings', icon: FiCalendar, label: 'Vet Bookings' },
  { path: '/ai-training', icon: FiCpu, label: 'AI Training' },
  { path: '/marketing', icon: FiTrendingUp, label: 'Marketing' },
  { path: '/organizations', icon: FiGrid, label: 'Organizations' },
  { path: '/orders', icon: FiShoppingBag, label: 'Orders' },
  { path: '/subscriptions', icon: FiCreditCard, label: 'Subscriptions' },
  { path: '/payments', icon: FiDollarSign, label: 'Payments' },
  { path: '/plans', icon: FiTag, label: 'Plans' },
  { path: '/posts', icon: FiFileText, label: 'Posts' },
  { path: '/missing-pets', icon: FiSearch, label: 'Missing Pets' },
  { path: '/reports', icon: FiAlertTriangle, label: 'Reports' },
  { path: '/sar', icon: FiShield, label: 'GDPR Requests' },
  { path: '/discount-codes', icon: FiTag, label: 'Discount Codes' },
  { path: '/notifications', icon: FiSend, label: 'Notifications' },
  { path: '/audit-log', icon: FiActivity, label: 'Audit Log' },
  { path: '/app-settings', icon: FiSliders, label: 'App Settings' },
  { path: '/settings', icon: FiSettings, label: 'Settings' },
];

const useUserAvatar = (uid) => {
  const [profile, setProfile] = useState(null);
  useEffect(() => {
    if (!uid) return undefined;
    const ref = doc(db, 'users', uid);
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) setProfile(snap.data());
    });
    return () => unsub();
  }, [uid]);
  return profile;
};

const Sidebar = ({ collapsed, onToggle, isMobile = false, drawerOpen = false, onCloseDrawer }) => {
  const { user } = useAuth();
  const profile = useUserAvatar(user?.uid);

  const avatarUrl =
    profile?.avatar ||
    profile?.photoURL ||
    profile?.profilePicture ||
    null;

  const initial = (
    profile?.firstName?.[0] ||
    user?.displayName?.[0] ||
    user?.email?.[0] ||
    'A'
  ).toUpperCase();

  const handleLogout = async () => {
    try {
      await logoutAdmin();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // On mobile we treat as a drawer: hidden off-screen by default, slid in when drawerOpen=true.
  const sidebarClass = [
    styles.sidebar,
    collapsed ? styles.collapsed : '',
    isMobile ? styles.mobile : '',
    isMobile && drawerOpen ? styles.drawerOpen : '',
  ].filter(Boolean).join(' ');

  return (
    <aside className={sidebarClass} aria-hidden={isMobile && !drawerOpen}>
      <div className={styles.header}>
        <div className={styles.logo}>
          <div className={styles.logoIcon}>
            <img src="/dierend-logo.png" alt="Dierend" />
          </div>
          {!collapsed && <span className={styles.logoText}>Dierend Admin</span>}
        </div>
        {isMobile ? (
          <button className={styles.toggleBtn} onClick={onCloseDrawer} aria-label="Close menu">
            <FiX />
          </button>
        ) : (
          <button className={styles.toggleBtn} onClick={onToggle}>
            {collapsed ? <FiChevronRight /> : <FiChevronLeft />}
          </button>
        )}
      </div>

      <nav className={styles.nav}>
        {menuItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === '/'}
            className={({ isActive }) =>
              `${styles.navItem} ${isActive ? styles.active : ''}`
            }
            title={collapsed ? item.label : ''}
          >
            <item.icon className={styles.navIcon} />
            {!collapsed && <span>{item.label}</span>}
          </NavLink>
        ))}
      </nav>

      <div className={styles.footer}>
        <div className={styles.userInfo}>
          <div className={styles.avatar}>
            {avatarUrl ? (
              <img src={avatarUrl} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            ) : (
              initial
            )}
          </div>
          {!collapsed && (
            <div className={styles.userDetails}>
              <span className={styles.userName}>
                {profile?.firstName
                  ? `${profile.firstName}${profile.lastName ? ' ' + profile.lastName : ''}`
                  : user?.displayName || 'Admin'}
              </span>
              <span className={styles.userEmail}>{user?.email}</span>
            </div>
          )}
        </div>
        <button
          className={styles.logoutBtn}
          onClick={handleLogout}
          title="Logout"
        >
          <FiLogOut />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
